import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        if (!detail.newProduct) return;

        const newMediaGallery = detail.html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Instantly previews a selected variant image while the native Shopify update runs.
   * @param {{mediaId?: string, mediaUrl: string, mediaSrcset?: string}} media
   */
  previewVariantMedia({ mediaId, mediaUrl, mediaSrcset }) {
    if (!mediaUrl) return;

    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    const currentVariantMediaId = this.dataset.selectedVariantMediaId;

    let image = null;
    let mediaElement = null;

    if (isDesktop) {
      if (currentVariantMediaId) {
        mediaElement = this.querySelector(
          `.media-gallery__grid .product-media[data-media-id="${currentVariantMediaId}"]`
        );
      }

      if (!mediaElement && mediaId) {
        mediaElement = this.querySelector(
          `.media-gallery__grid .product-media[data-media-id="${mediaId}"]`
        );
      }

      image = mediaElement?.querySelector('.product-media__image') ?? null;
    } else {
      let targetSlide = null;

      if (currentVariantMediaId) {
        targetSlide = this.querySelector(
          `slideshow-slide:has(.product-media[data-media-id="${currentVariantMediaId}"])`
        );
      }

      if (!targetSlide && mediaId) {
        targetSlide = this.querySelector(
          `slideshow-slide:has(.product-media[data-media-id="${mediaId}"])`
        );
      }

      if (!targetSlide) {
        targetSlide =
          this.querySelector('slideshow-slide[aria-hidden="false"]') ||
          this.querySelector('slideshow-slide');
      }

      mediaElement = targetSlide?.querySelector('.product-media') ?? null;
      image = mediaElement?.querySelector('.product-media__image') ?? null;

      if (targetSlide) {
        const slides = Array.from(this.querySelectorAll('slideshow-slide'));
        const targetIndex = slides.indexOf(targetSlide);
        if (targetIndex >= 0) {
          this.slideshow?.select(targetIndex, undefined, { animate: false });
        }
      }
    }

    if (!(image instanceof HTMLImageElement)) return;

    if (mediaSrcset) {
      image.setAttribute('srcset', mediaSrcset);
      image.setAttribute('sizes', isDesktop ? '50vw' : '100vw');
    } else {
      image.removeAttribute('srcset');
    }

    image.setAttribute('src', mediaUrl);
    image.setAttribute('data-max-resolution', mediaUrl);
    image.setAttribute('fetchpriority', 'high');
    image.decoding = 'async';

    if (mediaElement && mediaId) {
      mediaElement.setAttribute('data-media-id', mediaId);
    }

    if (currentVariantMediaId) {
      const zoomMedia = this.refs.zoomDialogComponent?.querySelector(
        `.product-media[data-media-id="${currentVariantMediaId}"]`
      );
      const zoomImage = zoomMedia?.querySelector('.product-media__image');

      if (zoomImage instanceof HTMLImageElement) {
        if (mediaSrcset) zoomImage.setAttribute('srcset', mediaSrcset);
        else zoomImage.removeAttribute('srcset');

        zoomImage.setAttribute('src', mediaUrl);
        zoomImage.setAttribute('data-max-resolution', mediaUrl);
        if (mediaId) zoomMedia.setAttribute('data-media-id', mediaId);
      }
    }

    if (mediaId) this.dataset.selectedVariantMediaId = mediaId;
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
